import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, CardMedia, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import NoImage from 'src/Resources/Images/shop/NoImage.png'
import { TransformerType } from '../../types'
import useModals from 'src/Hooks/useModals'
import { useShop } from '../../hooks'
import { Product } from '../../models'

interface ProductPowerMeterComponentProps {
  selectedTransformer: string | null
  onSelectedTransformerChange: (transformerId: string) => void
  powerMeterId: string
}

const useStyles = makeStyles()(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  info: {
    color: theme.palette.shades.shade02,
  },
  labelContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  productImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    margin: 0,
    padding: 0,
  },
  imageContainer: {
    margin: 0,
    padding: 0,
    width: '64px',
    height: '64px',
    minWidth: '64px',
    minHeight: '64px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedTransformerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

/*
 * This component shows part of the Power Meter when we order a powermeter with a transformer as a single product order.
 */
const ProductPowerMeterComponent: React.FC<ProductPowerMeterComponentProps> = ({
  selectedTransformer,
  powerMeterId,
  onSelectedTransformerChange,
}) => {
  const { t } = useCustomTranslation()
  const { openShopTransformerModal } = useModals()
  const { classes } = useStyles()
  const { getShopProduct } = useShop()
  const [transformerData, setTransformerData] = useState<Product | null>(null)

  const handleTransformer = (): void => {
    openShopTransformerModal({
      transformerType: TransformerType.SingleProduct,
      id: null,
      powerMeterId,
      onSingleProductAdd: selectedTransformerId => {
        onSelectedTransformerChange(selectedTransformerId)
      },
    })
  }

  useEffect(() => {
    selectedTransformer &&
      getShopProduct(selectedTransformer).then(result => {
        setTransformerData(result?.product as Product)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTransformer])

  const image = transformerData?.cover?.media?.url ?? NoImage

  return (
    <Box className={classes.container}>
      <Box className={classes.labelContainer}>
        <Typography className={classes.info} variant="h5">
          {t('shop.product-power-meter.select')}
        </Typography>
      </Box>
      {selectedTransformer && transformerData ? (
        <Box className={classes.selectedTransformerContainer}>
          <Box className={classes.imageContainer}>
            <CardMedia className={classes.productImage} component="img" image={image ? image : NoImage} />
          </Box>
          <Typography typography="h3">{transformerData.translated?.name}</Typography>
          <Button
            variant="text"
            onClick={() => {
              handleTransformer()
            }}
          >
            {t('shop.product-power-meter.change')}
          </Button>
        </Box>
      ) : (
        <Button
          variant="contained"
          onClick={() => {
            handleTransformer()
          }}
        >
          {t('shop.product-power-meter.button-label')}
        </Button>
      )}
    </Box>
  )
}

export default ProductPowerMeterComponent
